// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Select.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
/** https://github.com/JedWatson/react-select
 * Modified : Add feature - adding new item which is not matched with any existing item for multiple/single mode
 */

/**
 * Value component of multi selectbox
 * Example :
 * <Select multi
 *         simpleValue
 *         disabled={this.state.disabled}
 *         value={this.state.value}
 *         placeholder="Select your favourite(s)"
 *         options={this.state.options}
 *         onChange={this.handleSelectChange}>
 * </Select>
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Input from '../Izenda.AutosizeInput';
import Option from './Izenda.Option';
import Value from './Izenda.Value';
import localizer from 'share/localization/Izenda.Localizer';
import UIContants from 'logic/data/Izenda.UIConstants';

import FloatAffixed from '../Izenda.FloatAffixed';
import ScrollBox from '../Izenda.ScrollBox';
import VirtualList from '../Izenda.VirtualList';
import LazyLoading from '../LazyLoading/Izenda.LazyLoading';
import LazyLoadingBar from '../LazyLoading/Izenda.LazyLoadingBar';
import { isEmpty, isNil, debounce } from 'lodash/fp';

const stringOrNode = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);
const DEFAULT_ITEM_HEIGHT = 30;
const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;
const CHECK_INDEX_BY_NUMBER = -1;
const BACKSPACE_KEYCODE = 8;
const TAB_KEYCODE = 9;
const ENTER_KEYCODE = 13;
const ESCAPE_KEYCODE = 27;

export default class Select extends Component {
  static displayName = 'Select';

  static propTypes = {
    autoBlur: PropTypes.bool,
    autofocus: PropTypes.bool, // autofocus the component on mount
    className: PropTypes.string, // className for the outer element
    clearAllText: stringOrNode, // title for the "clear" control when multi: true
    clearValueText: stringOrNode, // title for the "clear" control
    clearable: PropTypes.bool, // should it be possible to reset value
    delimiter: PropTypes.string, // delimiter to use to join multiple values for the hidden field value
    disabled: PropTypes.bool, // whether the Select is disabled or not
    filterOption: PropTypes.func, // method to filter a single option (option, filterString)
    // boolean to enable default filtering or function to filter the options array
    // ([options], filterString, [values])
    filterOptions: PropTypes.any,
    ignoreCase: PropTypes.bool, // whether to perform case-insensitive filtering
    inputProps: PropTypes.object, // custom attributes for the Input
    labelKey: PropTypes.string, // path of the label value in option objects
    matchProp: PropTypes.string, // (any|label|value) which option property to filter on
    multi: PropTypes.bool, // multi-value input
    name: PropTypes.string, // generates a hidden <input /> tag with this field name for html forms
    noResultsText: stringOrNode, // placeholder displayed when there are no matching search results
    onBlur: PropTypes.func, // onBlur handler: function (event) {}
    onBlurResetsInput: PropTypes.bool, // whether input is cleared on blur
    onChange: PropTypes.func, // onChange handler: function (newValue) {}
    onClose: PropTypes.func, // fires when the menu is closed
    onFocus: PropTypes.func, // onFocus handler: function (event) {}
    onInputChange: PropTypes.func, // onInputChange handler: function (inputValue) {}
    onOpen: PropTypes.func, // fires when the menu is opened
    onValueClick: PropTypes.func, // onClick handler for value labels: function (value, event) {}
    optionComponent: PropTypes.func, // option component to render in dropdown
    optionRenderer: PropTypes.func, // optionRenderer: function (option) {}
    options: PropTypes.any, // array of options
    placeholder: stringOrNode, // field placeholder, displayed when there's no value
    required: PropTypes.bool, // applies HTML5 required attribute when needed
    scalable: PropTypes.bool, // scalable input width
    searchable: PropTypes.bool, // whether to enable searching feature or not
    // pass the value to onChange as a simple value (legacy pre 1.0 mode),
    // defaults to false
    simpleValue: PropTypes.bool,
    style: PropTypes.object, // optional style to apply to the control
    tabIndex: PropTypes.string, // optional tab index of the control
    value: PropTypes.any, // initial field value
    valueComponent: PropTypes.func, // value component to render
    valueKey: PropTypes.string, // path of the label value in option objects
    valueRenderer: PropTypes.func, // valueRenderer: function (option) {}
    // onAddNew handler: function (newValue) { return: boolean}
    // return result when add new option to list options, if fail does not call set value
    onAddNew: PropTypes.func,
    isInsertOnBlur: PropTypes.bool, // insert new value when blur select
    textboxMode: PropTypes.bool, // make this select like text box
    openOnFocus: PropTypes.bool, // always open options menu on focus
    inputMaxHeight: PropTypes.number, // max height of control
    hiddenMenu: PropTypes.bool // always hidden menu options
  };

  static defaultProps = {
    clearable: true,
    clearAllText: localizer.getText('IZENDA_SELECT_CLEAR_ALL_TEXT'),
    clearValueText: localizer.getText('IZENDA_SELECT_CLEAR_VALUE_TEXT'),
    delimiter: ',',
    disabled: false,
    filterOptions: true,
    ignoreCase: true,
    inputProps: {},
    labelKey: 'label',
    matchProp: 'label',
    multi: false,
    noResultsText: localizer.getText('IZENDA_SELECT_NO_RESULT_TEXT'),
    onBlurResetsInput: true,
    optionComponent: Option,
    placeholder: localizer.getText('IZENDA_SELECT_PLACE_HOLDER'),
    required: false,
    scalable: true,
    searchable: true,
    simpleValue: false,
    valueComponent: Value,
    valueKey: 'value',
    options: [],
    textboxMode: false
  };

  constructor(props) {
    super(props);
    const { required, value, multi } = props;
    this.state = {
      inputValue: '',
      isFocused: false,
      isOpen: false,
      isPseudoFocused: false,
      required: required && this.handleRequired(value, multi)
    };
  }

  componentDidMount() {
    if (this.props.autofocus) {
      this.focus();
    }

    this.onChangeWithDebounce = debounce(UIContants.DEFAULT_DEBOUNCE_TIME, input => {
      this.handleKeyEnterInput(input);
    });
  }

  componentWillUpdate(nextProps, nextState) {
    if (nextState.isOpen !== this.state.isOpen) {
      const handler = nextState.isOpen ? nextProps.onOpen : nextProps.onClose;
      handler && handler();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // focus to the selected option
    const { isOpen, inputValue } = this.state;
    if (this.refs.scrollBox && this.scrollTop && isOpen && !this.hasScrolledToOption) {
      this.hasScrolledToOption = true;
    } else if (!isOpen) {
      this.hasScrolledToOption = false;
    }

    if (prevState.inputValue !== inputValue && this.props.onInputChange) {
      this.props.onInputChange(inputValue);
    }
    if (prevProps.disabled !== this.props.disabled) {
      this.setState({ isFocused: false }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  focus = () => {
    const { input } = this.refs;
    if (!input) return;
    input.focus();
  };

  blurInput = () => {
    const { input } = this.refs;
    if (!input) return;
    input.blur();
  };

  handleMouseDown = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    const { disabled, searchable } = this.props;
    if (disabled || (event.type === 'mousedown' && event.button !== NUMBER_ZERO)) {
      return;
    }

    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();

    // for the non-searchable select, toggle the menu
    if (!searchable) {
      this.focus();
      return this.setState({
        isOpen: !this.state.isOpen
      });
    }

    // focus to open one dropdownlist at the same time
    this.focus();

    if (event.target.className === 'Select-arrow-zone' || event.target.className === 'Select-arrow') {
      // if the input is focused, ensure the menu is open
      this.setState({
        isOpen: true,
        isPseudoFocused: false
      });
    }
  };

  handleMouseDownOnArrow = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    const { disabled, searchable, isLazyLoading } = this.props;

    // in case of not searchable selects the "onMouseDown" handler do the same in method this.renderInputValue
    if (disabled || !searchable || this.isLoading || (event.type === 'mousedown' && event.button !== NUMBER_ZERO)) {
      return;
    }
    // If the menu isn't open, let the event bubble to the main handleMouseDown
    if (!this.state.isOpen) {
      if (isLazyLoading) {
        this.isLoading = true;
        this.onChangeWithDebounce('');
      } else {
        this.setState({isOpen: true});
      }
      return;
    }
    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();
    // close the menu
    this.closeMenu();
  };

  handleMouseDownOnMenu = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (this.props.disabled || (event.type === 'mousedown' && event.button !== NUMBER_ZERO)) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    this.focus();
  };

  closeMenu = () => {
    this.setState({
      isOpen: false,
      isPseudoFocused: this.state.isFocused && !this.props.multi,
      inputValue: ''
    });
    this.hasScrolledToOption = false;
    this.props.onCloseMenu && this.props.onCloseMenu();
  };

  handleInputFocus = event => {
    const isOpen = this.state.isOpen || this.props.openOnFocus;
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
    this.setState({
      isFocused: true,
      isOpen: isOpen
    });
  };

  handleInputBlur = () => {
    const { menu } = this.refs;
    const { isInsertOnBlur, valueKey, labelKey, onBlurResetsInput } = this.props;
    const { inputValue } = this.state;
    if (menu && document.activeElement.isEqualNode(menu)) {
      return;
    }

    if (this.props.onBlur) {
      if (isInsertOnBlur) {
        let newObject = {};
        newObject[valueKey] = inputValue;
        newObject[labelKey] = inputValue;
        this.setState({
          inputValue: ''
        });
        this.props.onBlur(newObject);
        this.addValue(newObject);
      }
    }
    const onBlurredNextState = {
      isFocused: false,
      isPseudoFocused: false
    };
    if (onBlurResetsInput) {
      onBlurredNextState.inputValue = '';
    }
    this.setState(onBlurredNextState);
  };

  handleInputChange = event => {
    const {
      target: { value }
    } = event;
    const newState = this.props.isLazyLoading
      ? {
          inputValue: value
        }
      : {
          isOpen: !isEmpty(value),
          isPseudoFocused: false,
          inputValue: value
        };

    this.isLoading = true;
    this.setState(newState);
    this.onChangeWithDebounce(value);
  };

  handleKeyDown = event => {
    const { disabled, clearable } = this.props;
    const { inputValue, isOpen } = this.state;

    if (disabled) return;
    switch (event.keyCode) {
      case BACKSPACE_KEYCODE:
        if (!inputValue) {
          event.preventDefault();
          this.popValue();
        }
        return;
      case TAB_KEYCODE:
        if (event.shiftKey || !isOpen) {
          return;
        }
        this.selectFocusedOption(false);
        return;
      case ENTER_KEYCODE:
        if (!isOpen) return;
        event.stopPropagation();
        this.selectFocusedOption(true);
        break;
      case ESCAPE_KEYCODE:
        if (isOpen) {
          this.closeMenu();
        } else if (clearable) {
          this.clearValue(event);
        }
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  handleValueClick = (option, event) => {
    if (!this.props.onValueClick) return;
    this.props.onValueClick(option, event);
  };

  handleRequired = (value, multi) => {
    if (!value) return true;
    return multi ? value.length === NUMBER_ZERO : Object.keys(value).length === NUMBER_ZERO;
  };

  getOptionLabel = op => {
    return op[this.props.labelKey];
  };

  getValueArray = () => {
    const { value, multi, delimiter } = this.props;
    let nextValue = value;
    if (multi) {
      if (typeof nextValue === 'string') nextValue = nextValue.split(delimiter);
      if (!Array.isArray(nextValue)) {
        if (nextValue === null || typeof(nextValue) === 'undefined') return [];
        nextValue = [value];
      }
      return nextValue.map(this.expandValue).filter(
        function(i) {
          return i;
        }.bind(this)
      );
    }
    const expandedValue = this.expandValue(nextValue);
    return expandedValue ? [expandedValue] : [];
  };

  expandValue = value => {
    if (typeof value !== 'string' && typeof value !== 'number') return value;
    const { options, valueKey } = this.props;
    if (!options) return;
    for (let i = 0; i < options.length; i++) {
      if (options[i][valueKey] === value) return options[i];
    }
  };

  setValue = value => {
    const { labelKey, multi, valueKey, delimiter, required, simpleValue, autoBlur } = this.props;
    const label = value && (value[labelKey] || null);
    const isNew = value && value.isNew;
    if (autoBlur) {
      this.blurInput();
    }
    if (!this.props.onChange) return;
    if (required) {
      this.setState({ required: this.handleRequired(value, multi) });
    }
    if (simpleValue && value) {
      value = multi
        ? value
            .map(
              function(i) {
                return i[valueKey];
              }.bind(this)
            )
            .join(delimiter)
        : value[valueKey];
    }
    this.props.onChange(value, label, isNew);
  };

  selectValue = value => {
    this.hasScrolledToOption = false;
    if (this.props.multi) {
      this.addValue(value);
      this.setState({
        inputValue: ''
      });
    } else {
      this.setValue(value);
      this.setState({
        isOpen: false,
        inputValue: '',
        isFocused: '',
        isPseudoFocused: false
      });
      this.blurInput();
      this.props.onCloseMenu && this.props.onCloseMenu();
    }
  };

  addValue = value => {
    const valueArray = this.getValueArray();
    this.setValue(valueArray.concat(value));
  };

  popValue = () => {
    const valueArray = this.getValueArray();
    if (!valueArray.length) return;
    if (valueArray[valueArray.length - NUMBER_ONE].clearableValue === false) return;
    this.setValue(valueArray.slice(NUMBER_ZERO, valueArray.length - NUMBER_ONE));
  };

  removeValue = value => {
    const valueArray = this.getValueArray();
    const { options } = this.props;
    this.setValue(
      valueArray.filter(
        function(i) {
          return i !== value;
        }.bind(this)
      )
    );

    // remove options of new item
    if (value.isNew) {
      const findIndex = options.indexOf(value);
      options.splice(findIndex, NUMBER_ONE);
    }
    this.focus();
  };

  clearValue = event => {
    // if the event was triggered by a mousedown and not the primary
    // button, ignore it.
    if (event && event.type === 'mousedown' && event.button !== NUMBER_ZERO) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.setValue(null);
    this.setState(
      {
        isOpen: false,
        inputValue: ''
      },
      this.focus
    );
  };

  focusOption = option => {
    this.setState({
      focusedOption: option
    });
  };

  selectFocusedOption = allowAddNew => {
    const { isLazyLoading, options, valueKey, labelKey, matchProp, multi } = this.props;
    if (isLazyLoading && this.props.onAddNew && !(options && options.length)) {
      this.setState({ isOpen: false });
    }

    const { inputValue } = this.state;
    let isMatch = false;
    if (this._focusedOption) {
      const valueTest = String(this._focusedOption[valueKey])
        .toLowerCase()
        .trim();
      const labelTest = String(this._focusedOption[labelKey])
        .toLowerCase()
        .trim();

      isMatch =
        (matchProp !== 'label' && valueTest == inputValue) || (matchProp !== 'value' && labelTest == inputValue);
    }

    let canAddNew = false;
    let newObject = { isNew: true };
    if (allowAddNew && !isMatch && this.props.onAddNew) {
      // Add new when don't exist
      newObject[valueKey] = inputValue;
      newObject[labelKey] = inputValue;
      canAddNew = this.props.onAddNew(newObject) !== false;
    }

    if (this._focusedOption && (!allowAddNew || !this.props.onAddNew || isMatch)) {
      return this.selectValue(this._focusedOption);
    } else if (canAddNew) {
      if (multi) {
        this.setState({
          inputValue: ''
        });
        return this.addValue(newObject);
      } else {
        this.selectValue(newObject);
      }
    }
  };

  renderValue = (valueArray, isOpen) => {
    const renderLabel = this.props.valueRenderer || this.getOptionLabel;
    const ValueComponent = this.props.valueComponent;
    const { multi, placeholder, onValueClick, disabled, valueKey } = this.props;
    if (!valueArray.length) {
      return !this.state.inputValue ? <div className='Select-placeholder'>{placeholder}</div> : null;
    }
    let onClick = onValueClick ? this.handleValueClick : null;
    if (multi) {
      return valueArray.map(
        function(value, i) {
          return (
            <ValueComponent
              disabled={disabled || value.clearableValue === false}
              key={'value-' + i + '-' + value[valueKey]}
              onClick={onClick}
              onRemove={this.removeValue}
              value={value}
            >
              {renderLabel(value)}
            </ValueComponent>
          );
        }.bind(this)
      );
    } else if (!this.state.inputValue) {
      if (isOpen) onClick = null;
      return (
        <ValueComponent disabled={disabled} onClick={onClick} value={valueArray[0]}>
          {renderLabel(valueArray[0])}
        </ValueComponent>
      );
    }
  };

  handleKeyEnterInput(inputValue) {
    const pageInfoDefault = { fromIndex: 0, pageSize: UIContants.LAZY_LOADING_DEFAULT_ITEMS_NUMBER };
    if (this.props.onSearchLazy) {
      this.props.onSearchLazy(inputValue, pageInfoDefault).then(data => {
        this.isLoading = false;
        if (!isEmpty(data) || this.props.onAddNew) {
          this.setState({ isOpen: true });
        }
      });
    } else {
      this.isLoading = false;
      this.setState({ isOpen: true });
    }
  }

  renderInput = () => {
    const { inputProps, disabled, searchable, tabIndex, scalable } = this.props;
    const { required, inputValue } = this.state;
    const className = classnames('Select-input', inputProps.className);

    if (disabled || !searchable) {
      return (
        <div
          {...inputProps}
          className={className}
          tabIndex={tabIndex || NUMBER_ZERO}
          onBlur={this.handleInputBlur}
          onFocus={this.handleInputFocus}
          ref='input'
          style={{ border: 0, width: 1, display: 'inline-block' }}
        />
      );
    }
    if (scalable) {
      return (
        <Input
          {...inputProps}
          className={className}
          tabIndex={tabIndex}
          onBlur={this.handleInputBlur}
          onChange={this.handleInputChange}
          onFocus={this.handleInputFocus}
          minWidth={5}
          ref='input'
          required={required}
          value={inputValue}
        />
      );
    }
    return (
      <Input
        {...inputProps}
        className={className}
        tabIndex={tabIndex}
        onBlur={this.handleInputBlur}
        onChange={this.handleInputChange}
        onFocus={this.handleInputFocus}
        ref='input'
        required={required}
        value={inputValue}
      />
    );
  };

  renderArrow() {
    return (
      <span className='Select-arrow-zone' onMouseDown={this.handleMouseDownOnArrow}>
        <span className='Select-arrow' onMouseDown={this.handleMouseDownOnArrow} />
      </span>
    );
  }

  filterOptions = excludeOptions => {
    let filterValue = this.state.inputValue;
    const options = this.props.options || [];
    const { ignoreCase, matchProp, valueKey, labelKey, isLazyLoading } = this.props;
    if (typeof this.props.filterOptions === 'function') {
      return this.props.filterOptions.call(this, options, filterValue, excludeOptions);
    } else if (this.props.filterOptions) {
      if (ignoreCase) {
        filterValue = filterValue.toLowerCase().trim();
      }
      if (excludeOptions)
        excludeOptions = excludeOptions.map(
          function(i) {
            return i[valueKey];
          }.bind(this)
        );
      return options.filter(
        function(option) {
          if (excludeOptions && excludeOptions.indexOf(option[valueKey]) > CHECK_INDEX_BY_NUMBER) return false;
          if (this.props.filterOption) return this.props.filterOption.call(this, option, filterValue);
          if (!filterValue) return true;
          if (filterValue && isLazyLoading) return true;
          let valueTest = String(option[valueKey]);
          let labelTest = String(option[labelKey]);
          if (ignoreCase) {
            if (matchProp !== 'label') valueTest = valueTest.toLowerCase();
            if (matchProp !== 'value') labelTest = labelTest.toLowerCase();
          }
          return (
            (matchProp !== 'label' && valueTest.indexOf(filterValue) >= NUMBER_ZERO) ||
            (matchProp !== 'value' && labelTest.indexOf(filterValue) >= NUMBER_ZERO)
          );
        }.bind(this)
      );
    } else {
      return options;
    }
  };

  getOptionList(options, valueArray, focusedOption) {
    const numOptions = options.length;
    let isSelected = false;

    return options.map(function(item, index) {
      isSelected = valueArray && valueArray.indexOf(item) > CHECK_INDEX_BY_NUMBER;

      return {
        isFirstItem: index === NUMBER_ZERO,
        isLastItem: index === numOptions - NUMBER_ONE,
        option: item,
        isSelected: isSelected,
        isFocused: item === focusedOption
      };
    });
  }

  renderOption = optionInfo => {
    const Option = this.props.optionComponent;
    const renderLabel = this.props.optionRenderer || this.getOptionLabel;
    const optionRef = optionInfo.isFocused ? 'focused' : null;
    const optionClass = classnames('Select-option overflow-text', {
      'is-selected': optionInfo.isSelected,
      'is-focused': optionInfo.isFocused,
      'is-disabled': optionInfo.option.disabled
    });

    if (isNil(renderLabel(optionInfo.option)) || isEmpty(renderLabel(optionInfo.option))) {
      return null;
    }

    return (
      <Option
        className={optionClass}
        isDisabled={optionInfo.option.disabled}
        isFocused={optionInfo.isFocused}
        onSelect={this.selectValue}
        onFocus={this.focusOption}
        option={optionInfo.option}
        isSelected={optionInfo.isSelected}
        ref={optionRef}
        title={renderLabel(optionInfo.option)}
      >
        {renderLabel(optionInfo.option)}
      </Option>
    );
  };

  getOptionKey = option => {
    return option[this.props.valueKey];
  };

  renderItemLazy = option => {
    const renderLabel = this.props.optionRenderer || this.getOptionLabel;
    const valueArray = this.getValueArray();
    const isSelected = valueArray && valueArray.indexOf(option) > CHECK_INDEX_BY_NUMBER;
    const optionClass = classnames('Select-option overflow-text', {
      'is-disabled': option.disabled,
      'is-selected': isSelected
    });

    const label = renderLabel(option);
    let optionTooltip = option[this.props.optionTooltip];
    !optionTooltip && (optionTooltip = label);

    if (isEmpty(label)) {
      return null;
    }

    return (
      <Option
        className={optionClass}
        isDisabled={option.disabled}
        onSelect={this.selectValue}
        isSelected={isSelected}
        onFocus={this.focusOption}
        option={option}
        title={optionTooltip}
      >
        {label}
      </Option>
    );
  };

  registerLazyLoadingStore = store => {
    this.lazyLoadingStore = store;
  };

  renderMenu(options) {
    const props = this.props;
    let hasValue = true;
    if (
      props.noResultsText &&
      !(this._visibleOptions && this._visibleOptions.length && props.options && props.options.length)
    ) {
      if (!props.isLazyLoading) {
        hasValue = false;
      } else if (props.onSearchLazy) {
        hasValue = false;
      }
    }

    if (!hasValue || this.isLoading) {
      return props.isLazyLoading && this.isLoading ? (
        <LazyLoadingBar className='Select-option u_flex u_flexCenterAlignItems' />
      ) : (
        <div className='Select-noresults'>{this.state.inputValue + ' ' + this.props.noResultsText}</div>
      );
    }

    if (props.isLazyLoading) {
      return (
        <LazyLoading
          data={this._visibleOptions}
          isLastPage={props.isLastPage}
          getData={props.getData}
          renderItem={this.renderItemLazy}
          viewHeight={UIContants.dropdownMaxHeight}
          itemHeight={DEFAULT_ITEM_HEIGHT}
          registerLazyLoadingStore={this.registerLazyLoadingStore}
        />
      );
    }

    if (options && options.length) {
      return (
        <VirtualList
          dataSource={this.getOptionList.apply(this, arguments)}
          getItemKey={this.getOptionKey}
          renderItem={this.renderOption}
        />
      );
    } else {
      return null;
    }
  }

  renderHiddenField = valueArray => {
    if (!this.props.name) return;

    function stringifyValue(value) {
      if (typeof value === 'object') {
        return JSON.stringify(value);
      } else {
        return value;
      }
    }

    return valueArray.map(
      function(item, index) {
        return (
          <Input
            key={'hidden.' + index}
            type='hidden'
            ref={'value' + index}
            name={this.props.name}
            value={stringifyValue(item[this.props.valueKey])}
            disabled={this.props.disabled}
          />
        );
      }.bind(this)
    );
  };

  getFocusableOption = selectedOption => {
    const options = this._visibleOptions;
    if (!options.length) return;
    const focusedOption = this.state.focusedOption || selectedOption;
    if (focusedOption && options.indexOf(focusedOption) > CHECK_INDEX_BY_NUMBER) return focusedOption;
    for (let i = 0; i < options.length; i++) {
      if (!options[i].disabled) return options[i];
    }
  };

  renderSelectWithScroll(valueArray, isOpen) {
    return (
      <div
        ref='control'
        className='Select-control'
        style={this.props.style}
        onKeyDown={this.handleKeyDown}
        onMouseDown={this.handleMouseDown}
      >
        <ScrollBox autoHeight autoHeightMax={this.props.inputMaxHeight}>
          {this.renderValue(valueArray, isOpen)}
          {this.renderInput(valueArray)}
        </ScrollBox>
        {!this.props.textboxMode && this.renderArrow()}
      </div>
    );
  }

  renderInputValue(valueArray, isOpen) {
    return (
      <div
        ref='control'
        className='Select-control'
        style={this.props.style}
        onKeyDown={this.handleKeyDown}
        onMouseDown={this.handleMouseDown}
      >
        {this.renderValue(valueArray, isOpen)}
        {this.renderInput(valueArray)}
        {!this.props.textboxMode && this.renderArrow()}
      </div>
    );
  }

  getSelectMenuStyle(options) {
    const items =
      isEmpty(options) && this.props.noResultsText
        ? NUMBER_ONE : Math.min(options.length, UIContants.DEFAUFT_DROPDOWN_ITEMS);

    return { height: items * DEFAULT_ITEM_HEIGHT };
  }

  render() {
    const { multi, disabled, searchable, inputMaxHeight, hiddenMenu } = this.props;
    const { isFocused, isOpen, isPseudoFocused } = this.state;
    const valueArray = this.getValueArray();
    const options = (this._visibleOptions = this.filterOptions(multi ? valueArray : null));
    const focusedOption = (this._focusedOption = this.getFocusableOption(valueArray[0]));
    const className = classnames('Select', this.props.className, {
      'Select--multi': multi,
      'is-disabled': disabled,
      'is-focused': isFocused,
      'is-open': isOpen,
      'is-pseudo-focused': isPseudoFocused,
      'is-searchable': searchable,
      'has-value': valueArray.length
    });

    const selectMenuStyle = this.getSelectMenuStyle(options);

    return (
      <div ref='wrapper' className={className}>
        {this.renderHiddenField(valueArray)}
        {inputMaxHeight ? this.renderSelectWithScroll(valueArray, isOpen) : this.renderInputValue(valueArray, isOpen)}
        {!hiddenMenu && isOpen && (
          <FloatAffixed anchor={this.refs.control} onParentScroll={this.closeMenu} onClickOutside={this.closeMenu}>
            <div ref='menuContainer' className='Select-menu-outer'>
              <ScrollBox autoHeight autoHeightMax={UIContants.dropdownMaxHeight} ref='scrollBox'>
                <div
                  ref='menu'
                  className='Select-menu'
                  style={selectMenuStyle}
                  onMouseDown={this.handleMouseDownOnMenu}
                >
                  {this.renderMenu(options, !multi ? valueArray : null, focusedOption)}
                </div>
              </ScrollBox>
            </div>
          </FloatAffixed>
        )}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/share/components/controls/multiSelectBox/Izenda.Select.js